import {
  Cluster,
  ClusterStats,
  defaultOnClusterClickHandler,
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer'
import { createCustomEqual, deepEqual } from 'fast-equals'
import React, { FC, useEffect, useRef, useState } from 'react'

import { RetailerFragment } from '@lib/umbraco/generated/types.generated'
import styles from './GoogleMap.module.scss'
interface MarkerWithRetailer {
  marker: google.maps.Marker
  retailer: RetailerFragment
}

interface GoogleMapProps extends google.maps.MapOptions {
  style: { [key: string]: string }
  onClick?: (e: google.maps.MapMouseEvent) => void
  onIdle?: (map: google.maps.Map) => void
  onRetailerClick?: (retailer: RetailerFragment) => void
  setVisibleRetailers?: (retailers: RetailerFragment[]) => void
  map?: google.maps.Map
  setMap?: (map: google.maps.Map) => void
  selectedRetailer: RetailerFragment
  setSelectedRetailer: (retailer: RetailerFragment) => void
  retailers?: RetailerFragment[]
  children?: React.ReactNode
}
let infoWindow: google.maps.InfoWindow
const GoogleMap: FC<GoogleMapProps> = ({
  onClick,
  onIdle,
  children,
  onRetailerClick,
  style,
  map,
  setMap,
  retailers,
  selectedRetailer,
  setSelectedRetailer,
  setVisibleRetailers,
  ...options
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [markers, setMarkers] = useState<MarkerWithRetailer[]>(null!)
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null)
  const mapStateRef = useRef<{
    zoom: number
    center: google.maps.LatLng | null
  }>({
    zoom: options.zoom || 3,
    center: null,
  })

  // Save map state whenever it changes
  useEffect(() => {
    if (map) {
      map.addListener('idle', () => {
        mapStateRef.current = {
          zoom: map.getZoom() || 3,
          center: map.getCenter() || null,
        }
      })
    }
  }, [map])

  useEffect(() => {
    if (ref.current && !map && setMap) {
      setMap(
        new window.google.maps.Map(ref.current, {
          ...options,
          zoom: mapStateRef.current.zoom,
          center: mapStateRef.current.center || options.center,
        }),
      )
    }
  }, [ref, map])

  useEffect(() => {
    if (selectedRetailer) {
      const marker = markers.find((marker) =>
        deepEqual(marker.retailer, selectedRetailer),
      )
      if (marker)
        google.maps.event.trigger(marker.marker, 'click', { event: marker })
    }
  }, [selectedRetailer])

  // let lastSetMarkers: IRetailer[] = null;

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      // Only update non-position related options
      const { zoom, center, ...otherOptions } = options
      map.setOptions(otherOptions)

      map.addListener('idle', () => {
        if (markers && setVisibleRetailers) {
          const newMarkers = markers
            .filter((marker) =>
              map.getBounds()!.contains(marker.marker.getPosition()!),
            )
            .filter((_, index) => index < 50)
            .map((marker) => marker.retailer)

          // if (arraysEqual(lastSetMarkers, newMarkers)) return;

          // lastSetMarkers = newMarkers;
          setVisibleRetailers(
            newMarkers.filter(
              (value, index, self) =>
                index === self.findIndex((t) => deepEqual(t, value)),
            ),
          )
        }
      })
    }
  }, [map, options, markers])

  React.useEffect(() => {
    if (map && retailers) {
      // Clear existing markers if any
      markers?.forEach(({ marker }) => marker.setMap(null))

      const newMarkers = createMarkers(retailers, onRetailerClick!, map)
      setMarkers(newMarkers)

      // Update existing clusterer or create new one
      if (clusterer) {
        clusterer.clearMarkers()
        clusterer.addMarkers(newMarkers.map((m) => m.marker))
      } else {
        const newClusterer = new MarkerClusterer({
          markers: newMarkers.map((m) => m.marker),
          map,
          renderer: interpolatedRenderer,
          algorithm: new SuperClusterAlgorithm({ radius: 125, maxZoom: 10 }),
          onClusterClick: (...args) => {
            infoWindow.close()
            setSelectedRetailer(null!)
            defaultOnClusterClickHandler(...args)
          },
        })
        setClusterer(newClusterer)
      }
    }
  }, [retailers, map])

  return (
    <>
      <div ref={ref} className={styles.map} style={style} />
    </>
  )
}
const deepCompareEqualsForMaps = createCustomEqual(
  (deepEqual) => (a: any, b: any) => {
    // TODO extend to other types

    // use fast-equals for other objects
    return deepEqual(a, b)
  },
)

const createMarkers = (
  retailers: RetailerFragment[],
  onRetailerClick: (retailer: RetailerFragment) => void,
  map: google.maps.Map,
) => {
  infoWindow = new google.maps.InfoWindow()

  const markers = retailers.map((retailer) => {
    const marker = new google.maps.Marker({
      position: {
        lat: Number.parseFloat(retailer.geoLatitude),
        lng: Number.parseFloat(retailer.geoLongitude),
      },
      icon: {
        url: '/icons/GUBI_pointer.png',
        scaledSize: new google.maps.Size(40, 40),
      },
    })

    google.maps.event.addListener(
      marker,
      'click',
      (e: {
        event: { marker: google.maps.Marker; retailer: RetailerFragment }
      }) => {
        infoWindow.close()
        if (e.event) {
          smoothZoom(
            map,
            12,
            map.getZoom(),
            new google.maps.LatLng(
              parseFloat(e.event.retailer.geoLatitude),
              parseFloat(e.event.retailer.geoLongitude),
            ),
          )
          infoWindow.setContent(e.event.retailer.name)
          infoWindow.open(map, e.event.marker)
        } else {
          infoWindow.setContent(retailer.name)
          infoWindow.open(map, marker)
          smoothZoom(
            map,
            12,
            map.getZoom(),
            //@ts-ignore
            new google.maps.LatLng(e.latLng.lat(), e.latLng.lng()),
          )
          if (onRetailerClick) {
            onRetailerClick(
              retailers.find(
                (retailer) =>
                  //@ts-ignore
                  retailer.geoLatitude! == e.latLng.lat() &&
                  //@ts-ignore
                  retailer.geoLongitude == e.latLng.lng(),
              )!,
            )
          }
        }
      },
    )
    return { marker, retailer } as MarkerWithRetailer
  })

  return markers
}
const interpolatedRenderer = {
  palette: 'black',
  render: function (
    { count, position }: Cluster,
    stats: ClusterStats,
  ): google.maps.Marker {
    // use d3-interpolateRgb to interpolate between red and blue
    const color = 'black'

    // create svg url with fill color
    const svg = window.btoa(`
  <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
    <circle cx="120" cy="120" opacity=".8" r="70" />    
  </svg>`)

    // create marker using svg icon
    return new google.maps.Marker({
      position,
      icon: {
        url: `data:image/svg+xml;base64,${svg}`,
        scaledSize: new google.maps.Size(75, 75),
      },
      label: {
        text: String(count),
        color: 'rgba(255,255,255,0.9)',
        fontSize: '12px',
      },
      // adjust zIndex to be above other markers
      zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
    })
  },
}
function useDeepCompareMemoize(value: any) {
  const ref = React.useRef()

  if (!deepCompareEqualsForMaps(value, ref.current)) {
    ref.current = value
  }

  return ref.current
}

function useDeepCompareEffectForMaps(
  callback: React.EffectCallback,
  dependencies: any[],
) {
  React.useEffect(callback, dependencies.map(useDeepCompareMemoize))
}

export function smoothZoom(
  map: google.maps.Map,
  max: number,
  cnt: number,
  markerPosition: google.maps.LatLng,
) {
  if (cnt >= max) {
    return
  } else {
    let z = google.maps.event.addListener(
      map,
      'zoom_changed',
      function (event: any) {
        google.maps.event.removeListener(z)
        map.panTo(markerPosition)
        smoothZoom(map, max, cnt + 1, markerPosition)
      },
    )
    setTimeout(function () {
      map.setZoom(cnt)
    }, 150) // 80ms is what I found to work well on my system -- it might not work well on all systems
  }
}

export default GoogleMap
